import type { ProblemDetail } from '@oss/shared';

const API_BASE = import.meta.env['VITE_API_URL'] ?? '/api/v1';

export class ApiError extends Error {
  constructor(readonly problem: ProblemDetail) {
    super(problem.title);
    this.name = 'ApiError';
  }

  get status(): number {
    return this.problem.status;
  }

  /** Field-level messages from a 422, for binding to form inputs. */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.problem.errors ?? []).map((e) => [e.path, e.message]));
  }
}

interface Tokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Token storage.
 *
 * In memory for the access token, localStorage for the refresh token. The
 * access token is the one an XSS payload would want and it never touches
 * persistent storage; the refresh token has to survive a page reload for
 * "stay signed in" to mean anything, and it is single-use with reuse
 * detection on the server, so a stolen one burns itself on first replay.
 */
let accessToken: string | null = null;
const REFRESH_KEY = 'oss.refresh';

export const tokenStore = {
  set(tokens: Tokens): void {
    accessToken = tokens.accessToken;
    localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
  },
  clear(): void {
    accessToken = null;
    localStorage.removeItem(REFRESH_KEY);
  },
  getRefresh(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },
  getAccess(): string | null {
    return accessToken;
  },
};

/**
 * A single in-flight refresh, shared by every caller.
 *
 * When an access token expires, every query on the dashboard fails within the
 * same tick. Without this promise, fifteen widgets would each fire their own
 * refresh, fourteen of them would present an already-consumed token, and the
 * server's reuse detection would correctly revoke the whole family and log
 * the user out. Coalescing is not an optimisation here — it is a correctness
 * requirement of rotating refresh tokens.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = tokenStore.getRefresh();
    if (!refreshToken) return false;
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        tokenStore.clear();
        return false;
      }
      const data = (await response.json()) as Tokens;
      tokenStore.set(data);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers awaiting this promise
      // all observe the same result before a new attempt can begin.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Set for mutating field-app requests so a retry cannot duplicate work. */
  idempotencyKey?: string;
}

/**
 * Everything up to a successful `Response`: auth header, one coalesced refresh
 * on a 401, and problem-detail errors thrown as `ApiError`.
 *
 * Separate from `apiRequest` because not every response is JSON. A CSV export
 * has to reuse this exact path — it is an authenticated request like any other,
 * and a plain `<a href>` cannot carry a bearer token — but it must read the
 * body as a blob rather than parse it.
 */
async function requestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  // The custom keys are peeled off rather than spread through: `body` is
  // `unknown` here (it gets serialised below) and `query`/`idempotencyKey` are
  // ours, none of which belong in a RequestInit.
  const { body, query: _query, idempotencyKey, ...init } = options;

  const send = async (): Promise<Response> => {
    const headers = new Headers(options.headers);
    if (!headers.has('Accept')) headers.set('Accept', 'application/json');
    if (body !== undefined) headers.set('Content-Type', 'application/json');
    if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
    if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey);

    return fetch(url, {
      ...init,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };

  let response = await send();

  if (response.status === 401 && tokenStore.getRefresh()) {
    if (await refreshTokens()) {
      response = await send();
    }
  }

  if (!response.ok) {
    const problem = await response
      .json()
      .catch(() => ({ title: response.statusText, status: response.status, type: 'about:blank' }));
    throw new ApiError(problem as ProblemDetail);
  }

  return response;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestRaw(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface Download {
  blob: Blob;
  /** From Content-Disposition, so the server names the file, not the caller. */
  filename: string | null;
  /** The server hit its row cap and the file is not the whole answer. */
  truncated: boolean;
}

export async function apiDownload(
  path: string,
  query?: RequestOptions['query'],
): Promise<Download> {
  const response = await requestRaw(path, {
    method: 'GET',
    headers: { Accept: 'text/csv' },
    ...(query ? { query } : {}),
  });

  const disposition = response.headers.get('Content-Disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);

  return {
    blob: await response.blob(),
    filename: match?.[1] ?? null,
    truncated: response.headers.get('X-Export-Truncated') === 'true',
  };
}

/** Hands a fetched blob to the browser as a download, then releases the URL. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Not revoked synchronously: Safari cancels an in-flight download when the
  // object URL disappears in the same tick as the click.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    apiRequest<T>(path, { method: 'GET', ...(query ? { query } : {}) }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  // DELETE takes an optional body. Removing a second factor requires proving
  // you still hold it, and that proof has to travel somewhere other than the
  // query string, which lands in access logs.
  delete: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body }) }),
};
